import { CACHE_KEY_LIST, MAX_IMAGE_CACHE } from "@/constants/constants";
import { checkArrayIncludesValue } from "@/service/feature-flags/helpers";

export const getCacheKeys = (): string[] => {
  try {
    return JSON.parse(localStorage.getItem(CACHE_KEY_LIST) ?? "[]");
  } catch {
    return [];
  }
};

const setCacheKeys = (keys: string[]): void => localStorage.setItem(CACHE_KEY_LIST, JSON.stringify(keys));

export const removeCachedImage = (key: string): void => {
  const keys = getCacheKeys().filter((k) => k !== key);
  setCacheKeys(keys);
  localStorage.removeItem(key);
};

export const cacheImage = (key: string, base64: string): void => {
  const keys = getCacheKeys();

  if (!keys.includes(key)) {
    if (keys.length >= MAX_IMAGE_CACHE) {
      const oldestKey = keys.shift();
      if (oldestKey) {
        localStorage.removeItem(oldestKey);
      }
    }
    keys.push(key);
    setCacheKeys(keys);
  }

  localStorage.setItem(key, base64);
};

export const getCachedImage = (key: string): string | null => localStorage.getItem(key);

const checkFlagForAllConditionCombination = (config: { flagArray: string[]; projectType?: string; step?: string }): boolean => {
  const { flagArray, projectType, step } = config;
  if (checkArrayIncludesValue(flagArray, "all")) return true; // 'all' overrides everything
  // Check if the brand with projectType is included
  if (projectType) {
    if (checkArrayIncludesValue(flagArray, `all_${projectType.toLowerCase()}`)) {
      return true;
    }
  }
  // Check if the brand with projectType and step is included
  if (projectType && step) {
    if (checkArrayIncludesValue(flagArray, `all_all_${step}`) || checkArrayIncludesValue(flagArray, `all_${projectType}_${step}`)) {
      return true;
    }
  }
  return false; // Return false if none of the conditions match
};

const checkFlagForCombination = (config: { brandId: string; flagArray: string[]; projectType?: string; step?: string }): boolean => {
  const { brandId, flagArray, projectType, step } = config;
  const brandLower = brandId.toLowerCase();

  // Check brand level
  if (checkArrayIncludesValue(flagArray, brandLower)) {
    return true;
  }

  // Check if the brand with projectType is included
  if (projectType) {
    const combinedKey = `${brandLower}_${projectType.toLowerCase()}`;
    if (checkArrayIncludesValue(flagArray, `all_${projectType.toLowerCase()}`) || checkArrayIncludesValue(flagArray, combinedKey)) {
      return true;
    }
  }

  // Check if the brand with projectType and step is included
  if (projectType && step) {
    const combinedKey = `${brandLower}_${projectType.toLowerCase()}_${step.toLowerCase()}`;
    if (
      checkArrayIncludesValue(flagArray, `all_all_${step}`) ||
      checkArrayIncludesValue(flagArray, `all_${projectType}_${step}`) ||
      checkArrayIncludesValue(flagArray, combinedKey)
    ) {
      return true;
    }
  }

  return false; // Return false if none of the conditions match
};

/* Get flag value based upon passed config
  - featureFlags is required and contains flags from the cache service
  - keyName is required and maps to a key in the featureFlags object
  - brandId is required
  - projectType and step are optional
  - returns true if a match is found based upon the config, otherwise false
*/
export const checkIfFlagExistsForConfig = (config: {
  featureFlags: { [key: string]: string[] };
  keyName: string;
  brandId: string;
  projectType?: string;
  step?: string;
}): boolean => {
  const { featureFlags, keyName, brandId, projectType, step } = config;
  const flagArray = featureFlags[keyName];
  if (!flagArray) return false; // false if keyName not found
  if (checkArrayIncludesValue(flagArray, "none")) return false; // 'none' for no combination
  if (checkFlagForAllConditionCombination({ flagArray, projectType, step })) return true; // 'all' overrides everything
  return checkFlagForCombination({ brandId, flagArray, projectType, step });
};
